import {Directive, ElementRef, AfterViewInit, Renderer2, OnDestroy} from '@angular/core';
import {DomUtilsService} from '../../utils/dom-utils.service';

@Directive({
  selector: '[myAccordionNav]',
  standalone: false,
})
export class AccordionNavDirective implements AfterViewInit, OnDestroy {
  private slideTime = 250;
  private clickListener: (() => void) | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private domUtils: DomUtilsService,
  ) {}

  ngAfterViewInit() {
    // Find all lists that contain sublists
    const nav = this.el.nativeElement;
    const lists = this.domUtils.findElements(nav, 'ul');

    // For each UL, find its parent LI and add an arrow icon
    lists.forEach(ul => {
      const parentLi = ul.parentElement;
      if (parentLi && parentLi.tagName === 'LI') {
        // Add arrow icon to indicate expandable section
        const icon = this.renderer.createElement('i');
        this.renderer.addClass(icon, 'material-icons');
        this.renderer.addClass(icon, 'icon-has-ul');
        this.renderer.appendChild(icon, this.renderer.createText('arrow_drop_down'));
        this.renderer.appendChild(parentLi, icon);

        // Disable the link if it has a submenu
        const link = parentLi.querySelector('a');
        if (link) {
          this.renderer.listen(link, 'click', event => {
            event.preventDefault();
          });
        }

        // Hide the UL initially unless parent is marked as open
        if (!parentLi.classList.contains('open')) {
          ul.style.display = 'none';
        }
      }
    });

    // Set up accordion click handler
    this.clickListener = this.renderer.listen(nav, 'click', event => {
      this.handleAccordionClick(event);
    });
  }

  private handleAccordionClick(event: Event): void {
    const target = event.target as HTMLElement;
    const parentLi = this.domUtils.closest(target, 'li');

    if (!parentLi) return;

    // Find direct child UL (not using :scope for better browser compatibility)
    let subUl: HTMLElement | null = null;
    const children = Array.from(parentLi.children);
    for (const child of children) {
      if (child.tagName === 'UL') {
        subUl = child as HTMLElement;
        break;
      }
    }
    if (!subUl) return;

    const nav = this.el.nativeElement;
    const depth = this.domUtils.getParentDepth(parentLi, '[myAccordionNav]') + 1;

    // Find all ULs at the same or deeper level (except the current one)
    const allUls = this.domUtils.findElements(nav, 'ul');
    allUls.forEach(ul => {
      const ulDepth = this.domUtils.getParentDepth(ul, '[myAccordionNav]');
      if (ulDepth >= depth && ul !== subUl) {
        // Close this UL
        this.slideUp(ul);
        const ulParentLi = ul.parentElement;
        if (ulParentLi) {
          this.renderer.removeClass(ulParentLi, 'open');
        }
      }
    });

    // Toggle the current section
    // We already know parentLi has a UL (subUl), so no need to check again
    if (parentLi.classList.contains('open')) {
      this.renderer.removeClass(parentLi, 'open');
      this.slideUp(subUl);
    } else {
      this.renderer.addClass(parentLi, 'open');
      this.slideDown(subUl);
    }
  }

  private slideUp(element: HTMLElement): void {
    // Get current height
    const currentHeight = element.scrollHeight;

    // Set explicit height to enable transition
    element.style.height = `${currentHeight}px`;
    element.style.overflow = 'hidden';

    // Force reflow to ensure initial state is applied
    element.offsetHeight;

    // Set transition
    element.style.transition = `height ${this.slideTime}ms ease`;

    // Animate to closed state
    element.style.height = '0';

    // Clean up after animation and hide element
    setTimeout(() => {
      element.style.display = 'none';
      element.style.height = '';
      element.style.overflow = '';
      element.style.transition = '';
    }, this.slideTime);
  }

  private slideDown(element: HTMLElement): void {
    // Make element visible but with 0 height
    element.style.display = 'block';
    element.style.height = '0';
    element.style.overflow = 'hidden';

    // Get the target height after making it visible
    const targetHeight = element.scrollHeight;

    // Force reflow to ensure initial state is applied
    element.offsetHeight;

    // Set transition
    element.style.transition = `height ${this.slideTime}ms ease`;

    // Animate to open state
    element.style.height = `${targetHeight}px`;

    // Clean up after animation
    setTimeout(() => {
      element.style.height = 'auto'; // Use auto instead of empty string
      element.style.overflow = 'visible';
      element.style.transition = '';
    }, this.slideTime);
  }

  ngOnDestroy(): void {
    if (this.clickListener) {
      this.clickListener();
    }
  }
}
